/**
 * @file overlay-plate.ts
 * @description Плашка под надписью на фото (phase19.16, партия SB120, этап
 * 3а; uiux-sb120-stage3 §3 «Надписи на фото»; данные db-design-sb120-stage3
 * §2 `overlay.plate`): выбор [Нет][Тёмная][Светлая][Свой], плотность и
 * контраст по худшему случаю — плашка на чёрном и на белом фото (тот же
 * расчёт, что предупреждение shared `sectionElementWarnings`).
 *
 * Хранение: нет `plate` — тёмная 60 % (как было); нет `fill` — тёмная (#000);
 * `density: 0` — «Нет»; «Светлая» — свой #FFFFFF; пустой `plate` не пишется.
 */

import {
  BLACK,
  SECTION_LOOK_LIMITS,
  WHITE,
  colorRefHex,
  compositeColors,
  contrastRatio,
  requireHexColor,
  type SectionOverlay,
  type SiteColors,
  type SiteTextColor,
} from "@chaiberi/shared";

export type PlateChoice = "none" | "dark" | "light" | "custom";

/** «Светлая» плашка — свой белый. */
export const LIGHT_PLATE = "#FFFFFF";
/** Плотность «как было». */
export const PLATE_DENSITY_DEFAULT = SECTION_LOOK_LIMITS.overlayPlate.default;

/** Что выбрано сейчас. */
export function plateChoice(overlay: SectionOverlay): PlateChoice {
  const plate = overlay.plate;
  if (plate?.density === 0) return "none";
  const fill = plate?.fill;
  if (!fill) return "dark";
  if (fill.kind === "custom" && fill.color.toUpperCase() === LIGHT_PLATE) return "light";
  return "custom";
}

/** Цвет своей плашки (`#hex`); не своя — `null`. */
export function plateCustomColor(overlay: SectionOverlay, colors: SiteColors): string | null {
  const fill = overlay.plate?.fill;
  return plateChoice(overlay) === "custom" && fill ? colorRefHex(fill, colors) : null;
}

/** Надпись с плашкой; пустая плашка снимается. */
function withPlate(overlay: SectionOverlay, plate: NonNullable<SectionOverlay["plate"]>): SectionOverlay {
  const clean: NonNullable<SectionOverlay["plate"]> = {};
  if (plate.fill) clean.fill = plate.fill;
  if (plate.density !== undefined) clean.density = plate.density;
  const next = { ...overlay };
  if (Object.keys(clean).length > 0) next.plate = clean;
  else delete next.plate;
  return next;
}

/** Выбор готовой плашки: «Нет» — плотность 0; «Тёмная» — без заливки; «Светлая» — белая. */
export function choosePlate(overlay: SectionOverlay, choice: Exclude<PlateChoice, "custom">): SectionOverlay {
  const density = overlay.plate?.density;
  // С «Нет» на плашку — плотность «как было», а не 0.
  const keepDensity = density === 0 ? undefined : density;
  if (choice === "none") return withPlate(overlay, { ...overlay.plate, density: 0 });
  if (choice === "dark") return withPlate(overlay, { density: keepDensity });
  return withPlate(overlay, { fill: { kind: "custom", color: LIGHT_PLATE }, density: keepDensity });
}

/** Свой цвет плашки. */
export function setPlateColor(overlay: SectionOverlay, color: string): SectionOverlay {
  const density = overlay.plate?.density;
  return withPlate(overlay, { fill: { kind: "custom", color }, density: density === 0 ? undefined : density });
}

/** Плотность плашки; `undefined` — «как было» (60 %). */
export function setPlateDensity(overlay: SectionOverlay, density: number | undefined): SectionOverlay {
  return withPlate(overlay, { ...overlay.plate, density });
}

/** Цвет текста надписи «Авто» — светлый (как было: белый текст на тёмной плашке). */
const AUTO_TEXT: SiteTextColor = { kind: "role", role: "onDark" };

/** Худший контраст текста надписи: плашка поверх чёрного и белого фото. */
export function overlayWorstContrast(overlay: SectionOverlay, colors: SiteColors): number | null {
  try {
    const fill = requireHexColor(overlay.plate?.fill ? colorRefHex(overlay.plate.fill, colors) : "#000000");
    const alpha = fill.a * ((overlay.plate?.density ?? PLATE_DENSITY_DEFAULT) / 100);
    const color = overlay.typo?.main?.color ?? AUTO_TEXT;
    const text = requireHexColor(color.kind === "role" ? colors[color.role] : color.color);
    let worst = Infinity;
    for (const photo of [BLACK, WHITE]) {
      const bg = compositeColors({ ...fill, a: alpha }, photo);
      worst = Math.min(worst, contrastRatio(compositeColors(text, bg), bg));
    }
    return worst;
  } catch {
    return null;
  }
}
